/**
 * quick-view.js — <quick-view-drawer>, the shared product quick-view.
 *
 * Shell markup: snippets/quick-view-drawer.liquid (rendered ONCE per page at a transform-free root).
 * Content:      sections/quick-view.liquid, fetched per product via the Section Rendering API
 *               (`/products/<handle>?section_id=quick-view`) and cached per handle.
 *
 * WHY FETCH INSTEAD OF SERVER-RENDERING EVERY TILE: a PDP can link a dozen cross-sell products, and
 * each quick view carries a full image set + variants + description. Inlining all of that into every
 * PDP would cost real bytes on first paint for content most visitors never open. Fetching also gets
 * us Liquid's real `| money` (market/currency correct) instead of re-formatting cents in JS.
 *
 * Triggers: any element with `data-qv-open="<handle>"`, delegated on document so tiles injected later
 * (or cloned into another drawer) work with no re-binding.
 *
 * TWO CONTENT SOURCES, picked by the drawer shell (snippets/quick-view-drawer.liquid `source`):
 * - default — sections/quick-view.liquid, as above. The PDP's drawer. Unchanged.
 * - `data-qv-source="pdp"` (non-PDP hosts, e.g. the promo page) — the body is the PDP's OWN right
 *   column (.pdp__info: buy box, size guide + SIIYS, shipping & returns, product details), minus
 *   Styled With, so it's identical to the product page — including the settings saved in its
 *   template, which no separate fragment could see. The light fragment above still supplies the
 *   image rail + "Go to product page". The column is fetched from `/products/<h>?view=<view>`:
 *   the first time as the whole page, which also yields the PDP section's dynamic id
 *   (template--<id>__main, per template, can't be hardcoded); after that, just that section via
 *   ?section_id (≈22KB vs ≈128KB), the id remembered for the visit. A failed section fetch (stale
 *   id) falls back to the whole page and re-learns it. Found by class, so it survives the day the
 *   new PDP becomes the default template (only `view` changes).
 *   Why not on the PDP itself: its modal <template>s are looked up page-wide by key, and the PDP's
 *   own size guide would collide with the drawer's.
 *
 * A modal opened from inside the drawer (ss-modal) owns the keyboard while it's up — see onKeydown.
 *
 * Plain IIFE + HTMLElement + <script defer> — the theme's component convention (not Vite).
 */
(function () {
  if (customElements.get("quick-view-drawer")) return;

  var SCROLL_LOCK = "ss-scroll-lock";
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Ceiling on rail-drag flick speed, px/ms. See onRailMove for why this is not optional. */
  var MAX_VEL = 10;

  function clampVelocity(v) {
    return Math.max(-MAX_VEL, Math.min(MAX_VEL, v));
  }

  /** Shared across instances (there is only ever one) — handle → fragment HTML string. */
  var cache = Object.create(null);
  /** In-flight requests, so hovering a tile then clicking it doesn't fire two fetches. */
  var pending = Object.create(null);

  /** Content source, set by the (single) drawer from its shell attributes — see the header. */
  var config = { source: "fragment", view: "" };
  var SECTION_KEY = "qv-pdp-section:";

  function getText(url) {
    return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } }).then(function (res) {
      if (!res.ok) throw new Error("Quick view " + res.status);
      return res.text();
    });
  }

  function productUrl(handle, params) {
    var query = Object.keys(params)
      .filter(function (key) {
        return params[key];
      })
      .map(function (key) {
        return key + "=" + encodeURIComponent(params[key]);
      })
      .join("&");
    return "/products/" + encodeURIComponent(handle) + (query ? "?" + query : "");
  }

  /** The light fragment (sections/quick-view.liquid) → the .qv markup string. */
  function fetchFragment(handle) {
    return getText(productUrl(handle, { section_id: "quick-view" })).then(function (html) {
      // Shopify wraps the section in <div id="shopify-section-quick-view">. Parse it out so the
      // drawer holds only .qv — the wrapper's id would otherwise be duplicated if we ever render
      // this section on a page for real.
      var doc = new DOMParser().parseFromString(html, "text/html");
      var frag = doc.querySelector(".qv");
      if (!frag) throw new Error("Quick view markup missing");
      return frag.outerHTML;
    });
  }

  /* ---- data-qv-source="pdp": the PDP's own right column ---- */

  function storedSection() {
    try {
      return window.sessionStorage.getItem(SECTION_KEY + config.view) || "";
    } catch (e) {
      return "";
    }
  }

  function storeSection(id) {
    try {
      if (id) window.sessionStorage.setItem(SECTION_KEY + config.view, id);
      else window.sessionStorage.removeItem(SECTION_KEY + config.view);
    } catch (e) {
      // Storage blocked (private mode) — every open just fetches the whole page.
    }
  }

  function fetchPdpColumn(handle) {
    var id = storedSection();
    var wholePage = function () {
      return getText(productUrl(handle, { view: config.view }));
    };
    if (!id) return wholePage();
    return getText(productUrl(handle, { view: config.view, section_id: id })).catch(function () {
      storeSection(""); // stale id (theme / template changed) — learn it again from the whole page
      return wholePage();
    });
  }

  function composePdpView(fragmentHtml, pdpHtml) {
    var parser = new DOMParser();
    var qv = parser.parseFromString(fragmentHtml, "text/html").querySelector(".qv");
    var pdp = parser.parseFromString(pdpHtml, "text/html");
    var info = pdp.querySelector(".pdp__info");
    var body = qv && qv.querySelector(".qv__body");
    if (!body || !info) throw new Error("Quick view markup missing");

    // The PDP section's dynamic id, for the cheap section fetch next time.
    var wrapper = info.closest('[id^="shopify-section-"]');
    if (wrapper) storeSection(wrapper.id.replace("shopify-section-", ""));

    // Not in the drawer: the cross-sell row. Also every EXTERNAL script / link the blocks print for
    // themselves: injected scripts never run (the drawer shell loads what the column needs), but an
    // injected <link rel="modulepreload"> DOES fetch — Styled With's slider library, for nothing.
    // Inline scripts are kept, in case a block ever carries a data island.
    info.querySelectorAll("styled-with, script[src], link").forEach(function (el) {
      el.remove();
    });

    // The buy box behaves as the drawer's, not the page's (what bb.liquid's context:'quick-view'
    // does): no ?variant= address rewriting, colour swatches re-fetch INTO the drawer, and an h2 —
    // the host page owns its h1.
    info.querySelectorAll("buy-box[data-bb-context]").forEach(function (bb) {
      bb.setAttribute("data-bb-context", "quick-view");
    });
    info.querySelectorAll("a.bb__color[href]").forEach(function (swatch) {
      var m = /\/products\/([^/?#]+)/.exec(swatch.getAttribute("href"));
      if (m) swatch.setAttribute("data-qv-swatch", decodeURIComponent(m[1]));
    });
    info.querySelectorAll("h1.bb__title").forEach(function (h1) {
      var h2 = pdp.createElement("h2");
      h2.className = h1.className;
      h2.innerHTML = h1.innerHTML;
      h1.replaceWith(h2);
    });

    var foot = body.querySelector(".qv__foot");
    body.innerHTML = '<div class="qv__pdp">' + info.innerHTML + "</div>" + (foot ? foot.outerHTML : "");
    qv.classList.add("qv--pdp");
    return qv.outerHTML;
  }

  function fetchPdpView(handle) {
    return Promise.all([fetchFragment(handle), fetchPdpColumn(handle)]).then(function (parts) {
      return composePdpView(parts[0], parts[1]);
    });
  }

  function fetchQuickView(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (pending[handle]) return pending[handle];

    var req = (config.source === "pdp" ? fetchPdpView(handle) : fetchFragment(handle))
      .then(function (html) {
        cache[handle] = html;
        delete pending[handle];
        return html;
      })
      .catch(function (err) {
        delete pending[handle];
        throw err;
      });

    pending[handle] = req;
    return req;
  }

  class QuickViewDrawer extends HTMLElement {
    connectedCallback() {
      config.source = this.getAttribute("data-qv-source") || "fragment";
      config.view = this.getAttribute("data-qv-view") || "";

      this.panel = this.querySelector("[data-qv-panel]");
      this.content = this.querySelector("[data-qv-content]");
      this.status = this.querySelector("[data-qv-status]");
      this.statusText = this.querySelector("[data-qv-status-text]");
      this.grip = this.querySelector("[data-qv-grip]");

      this.isOpen = false;
      this.lastFocus = null;
      this.requestToken = 0;

      this.onDocClick = this.onDocClick.bind(this);
      this.onDocPointerOver = this.onDocPointerOver.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSelfClick = this.onSelfClick.bind(this);
      this.onGripDown = this.onGripDown.bind(this);
      this.onGripMove = this.onGripMove.bind(this);
      this.onGripUp = this.onGripUp.bind(this);
      this.onRailDown = this.onRailDown.bind(this);
      this.onRailMove = this.onRailMove.bind(this);
      this.onRailUp = this.onRailUp.bind(this);
      this.glide = this.glide.bind(this);

      document.addEventListener("click", this.onDocClick);
      document.addEventListener("pointerover", this.onDocPointerOver);
      document.addEventListener("keydown", this.onKeydown);
      this.addEventListener("click", this.onSelfClick);
      if (this.grip) this.grip.addEventListener("pointerdown", this.onGripDown);
      // Delegated: the rail is replaced on every render(), so binding it directly would go stale.
      this.addEventListener("pointerdown", this.onRailDown);

      this.watchCart();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onDocClick);
      document.removeEventListener("pointerover", this.onDocPointerOver);
      document.removeEventListener("keydown", this.onKeydown);
      this.removeEventListener("click", this.onSelfClick);
      if (this.grip)
        this.grip.removeEventListener("pointerdown", this.onGripDown);
      this.removeEventListener("pointerdown", this.onRailDown);
      this.stopGlide();
    }

    /* ---------------------------------------------------------------- triggers */

    onDocClick(event) {
      var trigger = event.target.closest("[data-qv-open]");
      if (!trigger || this.contains(trigger)) return;

      // Let modified clicks (new tab / new window) and non-primary buttons behave natively — the
      // trigger is a real <a href> to the PDP, which is also the no-JS floor.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      )
        return;

      // Drag-vs-click needs NO work here. Embla v8 registers its own CAPTURE-phase click listener on
      // the carousel viewport and calls stopPropagation() + preventDefault() when the click is the
      // tail of a drag, so a swipe's click never reaches this document-level listener at all.
      // (v7 exposed a public clickAllowed() to ask; v8 removed it because it handles this itself.)
      // The guard below is just good manners toward any other component that already claimed the event.
      if (event.defaultPrevented) return;

      event.preventDefault();
      this.open(trigger.getAttribute("data-qv-open"), trigger);
    }

    /** Warm the cache on hover so the drawer usually opens with the content already in hand. */
    onDocPointerOver(event) {
      var trigger =
        event.target.closest && event.target.closest("[data-qv-open]");
      if (!trigger || this.contains(trigger)) return;
      var handle = trigger.getAttribute("data-qv-open");
      if (handle && !cache[handle] && !pending[handle])
        fetchQuickView(handle).catch(function () {});
    }

    onSelfClick(event) {
      if (event.target.closest("[data-qv-close]")) {
        event.preventDefault();
        this.close();
        return;
      }

      // Colour swatches are sibling-navigate links (colours are separate products). Inside the drawer
      // we swap the content instead of navigating the page away.
      var swatch = event.target.closest("[data-qv-swatch]");
      if (swatch) {
        event.preventDefault();
        this.load(swatch.getAttribute("data-qv-swatch"));
        return;
      }

      var retry = event.target.closest("[data-qv-retry]");
      if (retry && this.currentHandle) {
        event.preventDefault();
        this.load(this.currentHandle);
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      // A modal opened from inside the drawer (size guide, shipping & returns…) owns the keyboard
      // while it's up: Esc closes only the modal, and the modal traps Tab itself. Can't happen on
      // the PDP (its drawer has no modal triggers), so this changes nothing there.
      if (document.querySelector("ss-modal.is-open")) return;
      if (event.key === "Escape") {
        this.close();
        return;
      }
      if (event.key === "Tab") this.trapFocus(event);
    }

    /* ---------------------------------------------------------------- open/close */

    open(handle, trigger) {
      if (!handle) return;
      this.lastFocus = trigger || document.activeElement;
      this.isOpen = true;
      this.classList.add("is-open");
      this.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add(SCROLL_LOCK);
      this.load(handle);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove("is-open");
      this.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove(SCROLL_LOCK);
      this.resetDrag();
      this.stopGlide();

      // Return focus to whatever opened the drawer.
      if (this.lastFocus && typeof this.lastFocus.focus === "function")
        this.lastFocus.focus();
      this.lastFocus = null;
    }

    load(handle) {
      this.currentHandle = handle;

      // Stale-response guard: swatch-hopping can leave an earlier fetch in flight. Only the newest
      // request is allowed to write into the drawer.
      var token = ++this.requestToken;
      var self = this;

      if (cache[handle]) {
        this.render(cache[handle]);
        return;
      }

      this.setStatus("Loading…", false);
      fetchQuickView(handle)
        .then(function (html) {
          if (token !== self.requestToken) return;
          self.render(html);
        })
        .catch(function () {
          if (token !== self.requestToken) return;
          self.setStatus(
            'Could not load this product. <button type="button" data-qv-retry>Try again</button>',
            true,
          );
        });
    }

    render(html) {
      // Any in-flight release glide is pointing at the rail we are about to destroy.
      this.stopGlide();
      this.content.innerHTML = html;
      this.clearStatus();

      // Reset every scroll container, or a swatch-swap inherits the previous product's scroll
      // position. There are three, and which one is live depends on the breakpoint: the content box
      // scrolls on mobile, while on desktop the two columns scroll independently.
      this.content.scrollTop = 0;
      var panes = this.content.querySelectorAll(".qv__media, .qv__body");
      for (var i = 0; i < panes.length; i++) {
        panes[i].scrollTop = 0;
        panes[i].scrollLeft = 0;
      }

      this.focusFirst();
    }

    setStatus(message, isError) {
      if (!this.status) return;
      this.statusText.innerHTML = message;
      this.status.hidden = false;
      this.status.classList.toggle("is-error", !!isError);
    }

    clearStatus() {
      if (this.status) this.status.hidden = true;
    }

    /* ---------------------------------------------------------------- focus */

    focusFirst() {
      var target = this.querySelector("[data-qv-close]");
      if (target) target.focus();
    }

    trapFocus(event) {
      var nodes = Array.prototype.filter.call(
        this.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        },
      );
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- rail drag-to-pan */

    /**
     * Mouse drag-to-pan on the image rail, ADDITIVE to native scrolling — wheel, trackpad, keyboard
     * and scrollbar all keep working untouched, because all this does is write scrollTop/scrollLeft.
     *
     * MOUSE ONLY (`pointerType === 'mouse'`). Touch already pans the rail natively with proper
     * momentum and rubber-banding; intercepting it would replace good platform behaviour with a
     * worse hand-rolled copy.
     *
     * Both axes are applied every move, so one code path covers the desktop layout (vertical column)
     * and the narrow layout (horizontal row) without asking which one is live — whichever axis can't
     * scroll simply ignores the write.
     */
    onRailDown(event) {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      var rail = event.target.closest(".qv__media");
      if (!rail || !this.contains(rail)) return;

      this.stopGlide();
      this.rail = rail;
      this.railDragging = true;
      this.railMoved = false;
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.velX = 0;
      this.velY = 0;
      this.lastMoveTime = event.timeStamp;

      // Stops the browser starting a native image drag-and-drop (which would otherwise hijack the
      // gesture the moment the cursor leaves the image).
      event.preventDefault();

      window.addEventListener("pointermove", this.onRailMove);
      window.addEventListener("pointerup", this.onRailUp);
      window.addEventListener("pointercancel", this.onRailUp);
    }

    onRailMove(event) {
      if (!this.railDragging) return;
      var dx = event.clientX - this.lastX;
      var dy = event.clientY - this.lastY;

      // Only commit to "dragging" past a few px, so a plain click doesn't flip the cursor or
      // suppress selection for no reason.
      if (!this.railMoved && Math.abs(dx) + Math.abs(dy) > 3) {
        this.railMoved = true;
        this.rail.classList.add("is-dragging");
      }
      if (!this.railMoved) return;

      this.rail.scrollLeft -= dx;
      this.rail.scrollTop -= dy;

      // Velocity in px/ms, for the release glide. Two guards, both load-bearing:
      //   · the divisor floor — pointermove events can share a timestamp, and dividing by 0 would
      //     seed the glide with Infinity.
      //   · MAX_VEL — a 1ms gap between two far-apart moves yields an absurd velocity that the
      //     glide then multiplies into thousands of px of overshoot. Clamping keeps a hard flick
      //     to roughly half a screen of travel.
      var dt = Math.max(1, event.timeStamp - this.lastMoveTime);
      this.velX = clampVelocity(dx / dt);
      this.velY = clampVelocity(dy / dt);
      this.lastMoveTime = event.timeStamp;
      this.lastX = event.clientX;
      this.lastY = event.clientY;
    }

    onRailUp() {
      if (!this.railDragging) return;
      this.railDragging = false;
      window.removeEventListener("pointermove", this.onRailMove);
      window.removeEventListener("pointerup", this.onRailUp);
      window.removeEventListener("pointercancel", this.onRailUp);

      if (!this.railMoved) {
        this.rail = null;
        return;
      }
      this.rail.classList.remove("is-dragging");
      this.startGlide();
    }

    /** Release momentum. Decays each frame and hard-stops below a floor, so the rAF loop always ends. */
    startGlide() {
      if (Math.abs(this.velX) < 0.05 && Math.abs(this.velY) < 0.05) {
        this.rail = null;
        return;
      }
      this.lastFrame = 0;
      this.glideId = requestAnimationFrame(this.glide);
    }

    glide(now) {
      if (!this.rail) return;
      // Scale by elapsed frame time so the glide decays at the same rate on 60Hz and 120Hz displays.
      var frame = this.lastFrame ? Math.min(32, now - this.lastFrame) : 16;
      this.lastFrame = now;

      this.rail.scrollLeft -= this.velX * frame;
      this.rail.scrollTop -= this.velY * frame;

      var decay = Math.pow(0.94, frame / 16);
      this.velX *= decay;
      this.velY *= decay;

      // The floor is what prevents the dead-zone loop: without it the velocity approaches zero
      // asymptotically and the rAF never stops.
      if (Math.abs(this.velX) < 0.02 && Math.abs(this.velY) < 0.02) {
        this.stopGlide();
        return;
      }
      this.glideId = requestAnimationFrame(this.glide);
    }

    stopGlide() {
      if (this.glideId) cancelAnimationFrame(this.glideId);
      this.glideId = null;
      this.velX = 0;
      this.velY = 0;
      this.rail = null;
    }

    /* ---------------------------------------------------------------- cart */

    /**
     * Close on add-to-cart, so UpCart's drawer isn't stacked on top of ours.
     *
     * Two paths because UpCart intercepts the add button's click at the CAPTURE phase and we can't
     * rely on seeing it: (1) `upcartSubscribeCartLoaded` when UpCart is present, (2) a plain submit
     * listener as the floor (native POST → page navigates to /cart anyway, but this keeps the drawer
     * from flashing back open on bfcache restore).
     */
    watchCart() {
      var self = this;
      this.addEventListener("submit", function () {
        self.close();
      });

      var tries = 0;
      var poll = setInterval(function () {
        if (typeof window.upcartSubscribeCartLoaded === "function") {
          clearInterval(poll);
          window.upcartSubscribeCartLoaded(function () {
            if (self.isOpen) self.close();
          });
        } else if (++tries > 40) {
          clearInterval(poll); // UpCart absent (or removed) — the submit listener above covers us.
        }
      }, 250);
    }

    /* ---------------------------------------------------------------- drag-to-dismiss (mobile) */

    onGripDown(event) {
      if (!this.isOpen || !this.panel) return;
      this.dragStartY = event.clientY;
      this.dragStartTime = event.timeStamp;
      this.dragDelta = 0;
      this.dragging = true;
      this.classList.add("qv-no-anim");
      this.grip.setPointerCapture(event.pointerId);
      window.addEventListener("pointermove", this.onGripMove);
      window.addEventListener("pointerup", this.onGripUp);
      window.addEventListener("pointercancel", this.onGripUp);
    }

    onGripMove(event) {
      if (!this.dragging) return;
      // Downward only — dragging up must not lift the sheet past its resting position.
      this.dragDelta = Math.max(0, event.clientY - this.dragStartY);
      this.panel.style.transform = "translateY(" + this.dragDelta + "px)";
    }

    onGripUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      window.removeEventListener("pointermove", this.onGripMove);
      window.removeEventListener("pointerup", this.onGripUp);
      window.removeEventListener("pointercancel", this.onGripUp);

      // Dismiss on either a long pull or a quick flick — distance alone feels sticky when someone
      // flicks fast and short, and velocity alone fires on an accidental twitch.
      var height = this.panel.offsetHeight || 1;
      var elapsed = Math.max(1, event.timeStamp - this.dragStartTime);
      var velocity = this.dragDelta / elapsed; // px per ms
      var dismiss = this.dragDelta > height * 0.25 || velocity > 0.5;

      this.resetDrag();
      if (dismiss) this.close();
    }

    resetDrag() {
      this.dragging = false;
      this.classList.remove("qv-no-anim");
      if (this.panel) this.panel.style.transform = "";
    }
  }

  customElements.define("quick-view-drawer", QuickViewDrawer);
})();
